import { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Building, User, Key, CreditCard } from 'lucide-react';

export default function Settings() {
  const { user, organization } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'organization' | 'api' | 'billing'>('profile');

  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
  });

  const [orgData, setOrgData] = useState({
    name: organization?.name || '',
    slug: organization?.slug || '',
  });

  const handleProfileSubmit = (e: FormEvent) => {
    e.preventDefault();
    // TODO: Implement profile update API call
    alert('Profile update would be implemented here');
  };

  const handleOrgSubmit = (e: FormEvent) => {
    e.preventDefault();
    // TODO: Implement organization update API call
    alert('Organization update would be implemented here');
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'organization', label: 'Organization', icon: Building },
    { id: 'api', label: 'API Keys', icon: Key },
    { id: 'billing', label: 'Billing', icon: CreditCard },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-2">Manage your account and preferences</p>
      </div>

      <div className="flex space-x-6">
        <div className="w-64 card p-4">
          <nav className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 card p-6">
          {activeTab === 'profile' && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Profile Settings</h2>
              <form onSubmit={handleProfileSubmit} className="space-y-6 max-w-2xl">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={profileData.fullName}
                    onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Picture
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-8 h-8 text-blue-600" />
                    </div>
                    <button type="button" className="btn btn-secondary px-4 py-2">
                      Upload New Picture
                    </button>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary px-6 py-2">
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {activeTab === 'organization' && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Organization Settings</h2>
              <form onSubmit={handleOrgSubmit} className="space-y-6 max-w-2xl">
                <div>
                  <label htmlFor="orgName" className="block text-sm font-medium text-gray-700">
                    Organization Name
                  </label>
                  <input
                    id="orgName"
                    type="text"
                    value={orgData.name}
                    onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="orgSlug" className="block text-sm font-medium text-gray-700">
                    Organization Slug
                  </label>
                  <input
                    id="orgSlug"
                    type="text"
                    value={orgData.slug}
                    onChange={(e) => setOrgData({ ...orgData, slug: e.target.value })}
                    className="input mt-1"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Used in your organization URL
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Logo
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Building className="w-8 h-8 text-gray-400" />
                    </div>
                    <button type="button" className="btn btn-secondary px-4 py-2">
                      Upload New Logo
                    </button>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary px-6 py-2">
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {activeTab === 'api' && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">API Keys</h2>
              <p className="text-gray-600 mb-6">
                Manage your API keys for programmatic access to the NorthCoast.ai platform
              </p>

              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900">Production API Key</p>
                    <button className="btn btn-secondary px-3 py-1 text-sm">
                      Regenerate
                    </button>
                  </div>
                  <code className="text-sm text-gray-600 bg-white px-3 py-2 rounded block">
                    ncai_prod_xxxxxxxxxxxxxxxxxxxxxxxx
                  </code>
                  <p className="text-xs text-gray-500 mt-2">
                    Created on {new Date().toLocaleDateString()}
                  </p>
                </div>

                <button className="btn btn-primary px-4 py-2">
                  Create New API Key
                </button>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Billing & Subscription</h2>

              <div className="mb-6">
                <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Free Plan</h3>
                      <p className="text-sm text-gray-600 mt-1">Currently active</p>
                    </div>
                    <button className="btn btn-primary px-4 py-2">
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Usage This Month</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Messages</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">1,234</p>
                    <p className="text-xs text-gray-500 mt-1">of 10,000 included</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Active Widgets</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">3</p>
                    <p className="text-xs text-gray-500 mt-1">of 5 included</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Storage</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">245 MB</p>
                    <p className="text-xs text-gray-500 mt-1">of 1 GB included</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
